/**
 * Pattern Admin Dashboard
 * =======================
 * 
 * Admin interface for managing classification patterns.
 * Features:
 * - List all patterns with search/filter
 * - Create/Edit/Delete patterns
 * - Pattern testing tool
 * - AI suggestion tool
 * - Pattern candidate review
 * - Import/Export patterns
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, Plus, Edit2, Trash2, Check, X, Download, Upload, 
  Lightbulb, TestTube, Brain, TrendingUp, ChevronDown, ChevronRight,
  AlertTriangle, Filter, RefreshCw, Sparkles, Target, Award
} from 'lucide-react';
import api from '../../utils/api';

// ============================================================================
// TYPES
// ============================================================================

interface Pattern {
  _id: string;
  pattern_id: string;
  event_id: string;
  event_type: string;
  match_keywords: string[];
  match_mcc: string[];
  amount_range: [number, number] | null;
  generic_category: string;
  correct_category: string;
  correct_subcategory: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  rationale: string;
  active: boolean;
  accept_count: number;
  reject_count: number;
  total_decisions: number;
  accept_rate: number | null;
  created_at: string;
  updated_at: string;
}

interface PatternCandidate {
  candidate_id: string;
  event_id: string;
  suggested_category: string;
  suggested_subcategory: string;
  suggested_keywords: string[];
  occurrence_count: number;
  source_reports: string[];
  sample_vendors: string[];
  status: string;
  created_at: string;
}

interface PatternTestResult {
  pattern_id: string;
  match_score: number;
  matched_keywords: string[];
  suggested_category: string;
  suggested_subcategory: string;
  confidence: string;
  rationale: string;
}

// ============================================================================
// PATTERN FORM MODAL
// ============================================================================

interface PatternFormProps {
  pattern?: Pattern | null;
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
}

function PatternFormModal({ pattern, onSave, onClose }: PatternFormProps) {
  const [formData, setFormData] = useState({
    pattern_id: pattern?.pattern_id || '',
    event_id: pattern?.event_id || 'smitty_supply_2025',
    event_type: pattern?.event_type || 'business_interruption',
    match_keywords: pattern?.match_keywords?.join(', ') || '',
    correct_category: pattern?.correct_category || '',
    correct_subcategory: pattern?.correct_subcategory || '',
    confidence: pattern?.confidence || 'MEDIUM',
    rationale: pattern?.rationale || '',
    active: pattern?.active ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const data = {
        ...formData,
        match_keywords: formData.match_keywords.split(',').map(k => k.trim()).filter(k => k)
      };
      await onSave(data);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save pattern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            {pattern ? 'Edit Pattern' : 'Create Pattern'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pattern ID *
              </label>
              <input
                type="text"
                value={formData.pattern_id}
                onChange={e => setFormData({...formData, pattern_id: e.target.value})}
                disabled={!!pattern}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., SM-026"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event ID *
              </label>
              <input
                type="text"
                value={formData.event_id}
                onChange={e => setFormData({...formData, event_id: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., smitty_supply_2025"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Match Keywords * (comma-separated)
            </label>
            <textarea
              value={formData.match_keywords}
              onChange={e => setFormData({...formData, match_keywords: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="keyword1, keyword2, keyword3"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter keywords that should trigger this pattern match
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correct Category *
              </label>
              <input
                type="text"
                value={formData.correct_category}
                onChange={e => setFormData({...formData, correct_category: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Environmental Remediation"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correct Subcategory *
              </label>
              <input
                type="text"
                value={formData.correct_subcategory}
                onChange={e => setFormData({...formData, correct_subcategory: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Soil Remediation"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confidence Level
              </label>
              <select
                value={formData.confidence}
                onChange={e => setFormData({...formData, confidence: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="HIGH">HIGH - Very reliable</option>
                <option value="MEDIUM">MEDIUM - Generally reliable</option>
                <option value="LOW">LOW - Needs review</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={e => setFormData({...formData, active: e.target.checked})}
                  className="w-5 h-5 rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rationale * (Court-defensible explanation)
            </label>
            <textarea
              value={formData.rationale}
              onChange={e => setFormData({...formData, rationale: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Explain why this classification is appropriate..."
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Save Pattern
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// PATTERN TESTER
// ============================================================================

interface PatternTesterProps {
  onClose: () => void;
}

function PatternTester({ onClose }: PatternTesterProps) {
  const [vendor, setVendor] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [eventId, setEventId] = useState('smitty_supply_2025');
  const [results, setResults] = useState<PatternTestResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  const [loadingAi, setLoadingAi] = useState(false);

  const handleTest = async () => {
    setLoading(true);
    setResults([]);
    setAiSuggestion(null);
    
    try {
      const response = await api.post('/api/v1/pattern-admin/test', {
        vendor,
        description,
        amount: amount ? parseFloat(amount) : null,
        event_id: eventId
      });
      setResults(response.matches || []);
    } catch (err) {
      console.error('Pattern test failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAiSuggest = async () => {
    setLoadingAi(true);
    
    try {
      const response = await api.post('/api/v1/pattern-admin/ai-suggest', {
        vendor,
        description,
        amount: parseFloat(amount) || 0,
        current_category: null,
        event_context: 'business_interruption'
      });
      setAiSuggestion(response);
    } catch (err) {
      console.error('AI suggestion failed:', err);
    } finally {
      setLoadingAi(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div className="flex items-center gap-3">
            <TestTube className="text-white" size={24} />
            <h2 className="text-xl font-bold text-white">Pattern Testing Tool</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Input Form */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor Name
              </label>
              <input
                type="text"
                value={vendor}
                onChange={e => setVendor(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., EnviroClean Services"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount
              </label>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="e.g., 15000"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                rows={2}
                placeholder="e.g., Hazmat cleanup and soil remediation after explosion"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event ID
              </label>
              <input
                type="text"
                value={eventId}
                onChange={e => setEventId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="smitty_supply_2025"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleTest}
                disabled={!vendor || loading}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? <RefreshCw size={16} className="animate-spin" /> : <Target size={16} />}
                Test Patterns
              </button>
              <button
                onClick={handleAiSuggest}
                disabled={!vendor || loadingAi}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loadingAi ? <RefreshCw size={16} className="animate-spin" /> : <Brain size={16} />}
                AI Suggest
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="grid grid-cols-2 gap-6">
            {/* Pattern Matches */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Target size={18} className="text-purple-600" />
                Pattern Matches ({results.length})
              </h3>
              
              {results.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <Lightbulb size={32} className="mx-auto mb-2 text-gray-400" />
                  <p>No matches found</p>
                  <p className="text-sm">Try different keywords or use AI suggestion</p>
                </div>
              )}
              
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {results.map((result, idx) => (
                  <div 
                    key={result.pattern_id}
                    className={`p-4 rounded-lg border ${
                      idx === 0 ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">{result.pattern_id}</span>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          result.confidence === 'HIGH' ? 'bg-green-100 text-green-700' :
                          result.confidence === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {result.confidence}
                        </span>
                        <span className="text-sm font-medium text-blue-600">
                          {(result.match_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">{result.suggested_category}</span>
                      {' > '}
                      <span>{result.suggested_subcategory}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {result.matched_keywords.map((kw, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                          {kw}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 italic">{result.rationale}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Suggestion */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Brain size={18} className="text-indigo-600" />
                AI Suggestion
              </h3>
              
              {!aiSuggestion && !loadingAi && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <Sparkles size={32} className="mx-auto mb-2 text-gray-400" />
                  <p>No AI suggestion yet</p>
                  <p className="text-sm">Click "AI Suggest" to get Claude's recommendation</p>
                </div>
              )}
              
              {loadingAi && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <RefreshCw size={32} className="mx-auto mb-2 text-indigo-600 animate-spin" />
                  <p>Analyzing with Claude...</p>
                </div>
              )}
              
              {aiSuggestion && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border border-indigo-300 bg-indigo-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">Primary Suggestion</span>
                      <span className="text-sm font-medium text-indigo-600">
                        {(aiSuggestion.confidence * 100).toFixed(0)}% confidence
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">{aiSuggestion.suggested_category}</span>
                      {' > '}
                      <span>{aiSuggestion.suggested_subcategory}</span>
                    </div>
                    <p className="text-sm text-gray-700 bg-white/50 p-2 rounded">
                      {aiSuggestion.rationale}
                    </p>
                  </div>
                  
                  {aiSuggestion.alternative_categories?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Alternatives:</h4>
                      <div className="space-y-2">
                        {aiSuggestion.alternative_categories.map((alt: any, idx: number) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                            <div className="font-medium text-gray-900">
                              {alt.category} > {alt.subcategory}
                            </div>
                            <p className="text-gray-600 text-xs mt-1">{alt.reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN ADMIN DASHBOARD
// ============================================================================

export default function PatternAdminDashboard() {
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [candidates, setCandidates] = useState<PatternCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [filterConfidence, setFilterConfidence] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [activeTab, setActiveTab] = useState<'patterns' | 'candidates' | 'stats'>('patterns');
  
  // Modals
  const [showPatternForm, setShowPatternForm] = useState(false);
  const [editingPattern, setEditingPattern] = useState<Pattern | null>(null);
  const [showTester, setShowTester] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set(['smitty_supply_2025']));

  useEffect(() => {
    fetchData();
  }, [showInactive]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [patternsRes, candidatesRes] = await Promise.all([
        api.get(`/api/v1/pattern-admin/patterns?include_inactive=${showInactive}`),
        api.get('/api/v1/pattern-admin/candidates')
      ]);
      setPatterns(patternsRes.patterns || []);
      setCandidates(candidatesRes.candidates || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Group patterns by event
  const patternsByEvent = useMemo(() => {
    const groups: Record<string, Pattern[]> = {};
    patterns.forEach(p => {
      const event = p.event_id || 'unknown';
      if (!groups[event]) groups[event] = [];
      groups[event].push(p);
    });
    return groups;
  }, [patterns]);

  // Filter patterns
  const filteredPatterns = useMemo(() => {
    return patterns.filter(p => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matches = 
          p.pattern_id.toLowerCase().includes(query) ||
          p.correct_category.toLowerCase().includes(query) ||
          p.correct_subcategory.toLowerCase().includes(query) ||
          p.match_keywords.some(k => k.toLowerCase().includes(query));
        if (!matches) return false;
      }
      if (filterEvent && p.event_id !== filterEvent) return false;
      if (filterConfidence && p.confidence !== filterConfidence) return false;
      return true;
    });
  }, [patterns, searchQuery, filterEvent, filterConfidence]);

  // Handlers
  const handleSavePattern = async (data: any) => {
    if (editingPattern) {
      await api.put(`/api/v1/pattern-admin/patterns/${editingPattern.pattern_id}`, data);
    } else {
      await api.post('/api/v1/pattern-admin/patterns', data);
    }
    fetchData();
  };

  const handleDeletePattern = async (patternId: string) => {
    if (!confirm(`Are you sure you want to deactivate pattern ${patternId}?`)) return;
    await api.delete(`/api/v1/pattern-admin/patterns/${patternId}`);
    fetchData();
  };

  const handlePromoteCandidate = async (candidateId: string) => {
    const patternId = prompt('Enter Pattern ID for the new pattern (e.g., DISC-001):');
    if (!patternId) return;
    
    await api.post(`/api/v1/pattern-admin/candidates/${candidateId}/promote?pattern_id=${patternId}`);
    fetchData();
  };

  const handleRejectCandidate = async (candidateId: string) => {
    const reason = prompt('Reason for rejection (optional):');
    await api.post(`/api/v1/pattern-admin/candidates/${candidateId}/reject?reason=${encodeURIComponent(reason || '')}`);
    fetchData();
  };

  const toggleEventExpanded = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  // Stats
  const stats = useMemo(() => {
    const total = patterns.length;
    const active = patterns.filter(p => p.active).length;
    const highConf = patterns.filter(p => p.confidence === 'HIGH' && p.active).length;
    const withDecisions = patterns.filter(p => p.total_decisions > 0);
    const avgAcceptRate = withDecisions.length > 0 
      ? withDecisions.reduce((sum, p) => sum + (p.accept_rate || 0), 0) / withDecisions.length 
      : 0;
    
    return { total, active, highConf, avgAcceptRate, pendingCandidates: candidates.length };
  }, [patterns, candidates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Lightbulb className="text-yellow-500" size={28} />
              Pattern Library Admin
            </h1>
            <p className="text-gray-600 mt-1">Manage classification patterns and review discoveries</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTester(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              data-testid="open-pattern-tester"
            >
              <TestTube size={18} />
              Test Patterns
            </button>
            <button
              onClick={() => { setEditingPattern(null); setShowPatternForm(true); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              data-testid="create-pattern-btn"
            >
              <Plus size={18} />
              Add Pattern
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Patterns</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Active</div>
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">High Confidence</div>
            <div className="text-2xl font-bold text-blue-600">{stats.highConf}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Avg Accept Rate</div>
            <div className="text-2xl font-bold text-purple-600">
              {stats.avgAcceptRate > 0 ? `${(stats.avgAcceptRate * 100).toFixed(0)}%` : 'N/A'}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Pending Candidates</div>
            <div className="text-2xl font-bold text-orange-600">{stats.pendingCandidates}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('patterns')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'patterns' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Patterns ({stats.total})
          </button>
          <button
            onClick={() => setActiveTab('candidates')}
            className={`px-4 py-2 font-medium flex items-center gap-2 ${
              activeTab === 'candidates' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Discovered Candidates
            {stats.pendingCandidates > 0 && (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                {stats.pendingCandidates}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Patterns Tab */}
      {activeTab === 'patterns' && (
        <div className="bg-white rounded-lg shadow">
          {/* Filters */}
          <div className="p-4 border-b flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search patterns..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <select
              value={filterEvent}
              onChange={e => setFilterEvent(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">All Events</option>
              {Object.keys(patternsByEvent).map(event => (
                <option key={event} value={event}>{event}</option>
              ))}
            </select>
            <select
              value={filterConfidence}
              onChange={e => setFilterConfidence(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">All Confidence</option>
              <option value="HIGH">HIGH</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="LOW">LOW</option>
            </select>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-gray-600">Show Inactive</span>
            </label>
          </div>

          {/* Pattern List by Event */}
          <div className="divide-y divide-gray-200">
            {Object.entries(patternsByEvent).map(([eventId, eventPatterns]) => {
              const visiblePatterns = eventPatterns.filter(p => 
                filteredPatterns.includes(p)
              );
              
              if (visiblePatterns.length === 0) return null;
              
              return (
                <div key={eventId}>
                  <button
                    onClick={() => toggleEventExpanded(eventId)}
                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {expandedEvents.has(eventId) ? (
                        <ChevronDown size={20} className="text-gray-500" />
                      ) : (
                        <ChevronRight size={20} className="text-gray-500" />
                      )}
                      <span className="font-semibold text-gray-900">{eventId}</span>
                      <span className="text-sm text-gray-500">({visiblePatterns.length} patterns)</span>
                    </div>
                  </button>
                  
                  {expandedEvents.has(eventId) && (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                          <tr>
                            <th className="px-4 py-2 text-left">Pattern ID</th>
                            <th className="px-4 py-2 text-left">Keywords</th>
                            <th className="px-4 py-2 text-left">Category</th>
                            <th className="px-4 py-2 text-center">Confidence</th>
                            <th className="px-4 py-2 text-center">Decisions</th>
                            <th className="px-4 py-2 text-center">Accept Rate</th>
                            <th className="px-4 py-2 text-center">Status</th>
                            <th className="px-4 py-2 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {visiblePatterns.map(pattern => (
                            <tr key={pattern.pattern_id} className={`hover:bg-gray-50 ${!pattern.active ? 'opacity-50' : ''}`}>
                              <td className="px-4 py-3 font-medium text-gray-900">
                                {pattern.pattern_id}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1 max-w-[200px]">
                                  {pattern.match_keywords.slice(0, 3).map((kw, i) => (
                                    <span key={i} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                      {kw}
                                    </span>
                                  ))}
                                  {pattern.match_keywords.length > 3 && (
                                    <span className="text-xs text-gray-500">+{pattern.match_keywords.length - 3}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="font-medium text-gray-900">{pattern.correct_category}</div>
                                <div className="text-gray-500">{pattern.correct_subcategory}</div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  pattern.confidence === 'HIGH' ? 'bg-green-100 text-green-700' :
                                  pattern.confidence === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-orange-100 text-orange-700'
                                }`}>
                                  {pattern.confidence}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center text-sm text-gray-600">
                                {pattern.total_decisions || 0}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {pattern.accept_rate !== null ? (
                                  <span className={`text-sm font-medium ${
                                    pattern.accept_rate >= 0.8 ? 'text-green-600' :
                                    pattern.accept_rate >= 0.5 ? 'text-yellow-600' :
                                    'text-red-600'
                                  }`}>
                                    {(pattern.accept_rate * 100).toFixed(0)}%
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {pattern.active ? (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Active</span>
                                ) : (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">Inactive</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => { setEditingPattern(pattern); setShowPatternForm(true); }}
                                    className="p-1 text-gray-400 hover:text-blue-600"
                                    title="Edit"
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeletePattern(pattern.pattern_id)}
                                    className="p-1 text-gray-400 hover:text-red-600"
                                    title="Deactivate"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Candidates Tab */}
      {activeTab === 'candidates' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            {candidates.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Award size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">No Pattern Candidates</p>
                <p className="text-sm mt-1">
                  Candidates are discovered automatically when accountants make repeated reclassifications
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {candidates.map(candidate => (
                  <div 
                    key={candidate.candidate_id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                            {candidate.candidate_id}
                          </span>
                          <span className="text-sm text-gray-500">
                            {candidate.occurrence_count} occurrences across {candidate.source_reports.length} reports
                          </span>
                        </div>
                        <div className="text-lg font-medium text-gray-900">
                          {candidate.suggested_category} &gt; {candidate.suggested_subcategory}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {candidate.suggested_keywords.map((kw, i) => (
                            <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                              {kw}
                            </span>
                          ))}
                        </div>
                        <div className="text-sm text-gray-500 mt-2">
                          Sample vendors: {candidate.sample_vendors.slice(0, 3).join(', ')}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePromoteCandidate(candidate.candidate_id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                        >
                          <Check size={16} />
                          Promote
                        </button>
                        <button
                          onClick={() => handleRejectCandidate(candidate.candidate_id)}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-2"
                        >
                          <X size={16} />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showPatternForm && (
        <PatternFormModal
          pattern={editingPattern}
          onSave={handleSavePattern}
          onClose={() => { setShowPatternForm(false); setEditingPattern(null); }}
        />
      )}
      
      {showTester && (
        <PatternTester onClose={() => setShowTester(false)} />
      )}
    </div>
  );
}
